import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { SimpleGrid, useSimpleGrid } from "../../components/simple_grid";
import { UserDef } from "../../services/user_service";
import { useDrawer } from "../../components/use_drawer";
import { bindInputValue, useInfo } from "../../form_utils";
import { useCallback, useEffect, useMemo } from "react";
import { FieldItemDef } from "../../services/field_item_service";
import { DeleteButton } from "../../components/delete_button";
import { idRecord, idString } from "../../db_utils";
import { apiClient } from "../../services/api";
import { useToast } from "../../hooks/useToast";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
    },
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
});

/**
 * People are managed inside exactly one tenant. `tenantId` comes from the workbench URL and is the
 * only source of the write target: the old form fell back to the edited row's tenant and then to the
 * signed-in operator's own tenant, which is how a platform administrator could file a new account
 * into FRK-INTERNAL while believing they were creating it for the tenant on screen.
 */
/**
 * Decides what an EDIT submits about roles.
 *
 * Roles are sent only when the operator actually touched the dropdown (`info.groups` is an array).
 * Sending them on every save rewrites the group set during unrelated edits — a rename would submit
 * whatever happened to be pre-selected, and for a platform administrator that pre-selection is not
 * an assignable set at all, so the request is refused with 403 and the account becomes uneditable.
 * Omitting the field leaves the backend's groups untouched, which is what "I only renamed them"
 * should mean.
 *
 * Returned as explicit `undefined`s so the caller can Object.assign over a copied payload and have
 * JSON.stringify drop them.
 */
export const buildPersonUpdateGroupFields = (info, selectedGroups) => {
    if (!Array.isArray(info?.groups)) {
        return { role: undefined, groups: undefined };
    }

    const groups = selectedGroups.length ? selectedGroups : ["FieldOperator"];
    return {
        role: undefined,
        groups,
        // Field permissions only mean something for the on-site role; dropping that role drops them.
        ...(groups.includes("FieldOperator") ? {} : { fields: [] }),
    };
};

export const UserForm = ({ item, onChange, tenantId = null, tenantName = "" }) => {
    const styles = useStyles();
    const { info, setInfo, updateInfo } = useInfo({});
    const showToast = useToast();
    const fixedTenantId = Number.isInteger(Number(tenantId)) && Number(tenantId) > 0
        ? Number(tenantId)
        : null;
    const targetTenantId = fixedTenantId;
    const isPlatformAdminTarget = Boolean(item?.isPlatformAdmin);
    useEffect(() => {
        setInfo({});
    }, [item, setInfo]);
    const v = bindInputValue(item, info, setInfo);
    const canonicalizeGroup = (group) => {
        if (!group) {
            return null;
        }

        const map = {
            admin: "Admin",
            Admin: "Admin",
            QA: "QA",
            qa: "QA",
            Annotator: "Annotator",
            annotator: "Annotator",
            FieldOperator: "FieldOperator",
            fieldoperator: "FieldOperator",
            field_admin: "FieldOperator",
            "field-admin": "FieldOperator",
            BackendOperator: "BackendOperator",
            backendoperator: "BackendOperator",
            staff: "BackendOperator",
            operator: "BackendOperator",
        };

        return map[group] || null;
    };
    const sortGroups = (groups) => {
        if (!Array.isArray(groups)) {
            return [];
        }
        const unique = new Set();
        groups.forEach((value) => {
            const normalized = canonicalizeGroup(value) || groupOptions.find((option) => option.value === value)?.value;
            if (normalized) {
                unique.add(normalized);
            }
        });
        const ordered = groupOptions
            .map((option) => option.value)
            .filter((value) => unique.has(value));
        return ordered;
    };
    // The platform Admin group is NOT offered here. This flow cannot grant it (the API refuses with
    // 403) and must not revoke it either; granting and revoking live in the dedicated platform
    // administrator actions. Rendering a permanently-failing option only taught operators to expect
    // a capability this page does not have.
    const groupOptions = [
        { value: "QA", label: "质检员" },
        { value: "Rechecker", label: "复检员" },
        { value: "Annotator", label: "标注员" },
        { value: "FieldOperator", label: "现场操作员" },
        { value: "BackendOperator", label: "后台操作员" },
    ];
    // Both branches go through sortGroups, which keeps only the groups this form actually offers.
    // The role fallback used to bypass that filter, so a platform administrator — whose only group
    // is Admin and whose Role is "admin" — came back as ["Admin"], was pre-selected, and was then
    // submitted to the ordinary person endpoint, which refuses it with 403 every single time. The
    // account's Admin membership is preserved by the backend and shown separately; it is not an
    // editable option here.
    const initialGroups = useMemo(() => {
        if (Array.isArray(item?.groups) && item.groups.length > 0) {
            const normalized = sortGroups(item.groups);
            if (normalized.length > 0) {
                return normalized;
            }
        }
        return sortGroups([item?.role]);
    }, [item]);
    const selectedGroups = useMemo(() => {
        if (Array.isArray(info?.groups)) {
            return sortGroups(info.groups);
        }
        return initialGroups;
    }, [info, initialGroups]);

    const listAvailableFields = useCallback(async (client, filter) => {
        if (!fixedTenantId) {
            return [];
        }
        const fields = await apiClient.getManagedFields(fixedTenantId);
        if (!filter?.trim()) return fields || [];
        const term = filter.trim().toLowerCase();
        return (fields || []).filter((field) =>
            [field.seq, field.name].some((value) => value?.toLowerCase().includes(term))
        );
    }, [fixedTenantId]);
    const { items } = useSimpleGrid({ listItems: listAvailableFields });
    const columns = [
        FieldItemDef.createTableColumn("seq"),
        FieldItemDef.createTableColumn("name"),
    ];
    const onFieldsChange = (fields) => {
        fields = Array.from(fields).map((it) => idRecord(it));
        console.log(fields);
        updateInfo({ fields });
    };

    const onDelete = async () => {
        try {
            if (!targetTenantId) {
                throw new Error("A target tenant is required before changing a person.");
            }
            await apiClient.disableManagedPerson(fixedTenantId, item.id);
            showToast("用户已停用", "success");
            onChange();
        } catch (error) {
            console.error("Failed to delete user:", error);
            showToast("删除用户失败", "error", error.message);
        }
    };

    const onSave = async () => {
        try {
            if (!targetTenantId) {
                throw new Error("A target tenant is required before changing a person.");
            }
            const payload = { ...(info || {}) };
            payload.TenantId = targetTenantId;
            if (item) {
                Object.assign(payload, buildPersonUpdateGroupFields(info, selectedGroups));
                await apiClient.updateManagedPerson(fixedTenantId, item.id, {
                    ...payload,
                    tenantId: fixedTenantId,
                });
                showToast("用户信息已更新", "success");
                onChange();
            } else {
                // Create new user
                if (!payload.fields || payload.fields.length === 0) {
                    payload.fields = [];
                }
                const groupsForSave = selectedGroups.length
                    ? selectedGroups
                    : Array.isArray(payload.groups) && payload.groups.length
                        ? sortGroups(payload.groups)
                        : ["FieldOperator"];
                payload.groups = groupsForSave;
                delete payload.role;
                if (!groupsForSave.includes("FieldOperator")) {
                    payload.fields = [];
                }
                console.log("Creating user with data:", payload);
                await apiClient.createManagedPerson(fixedTenantId, {
                    ...payload,
                    tenantId: fixedTenantId,
                });
                showToast("用户创建成功", "success");
                onChange();
            }
        } catch (error) {
            console.error("Failed to save user:", error);
            showToast("保存用户失败", "error", error.message);
        }
    };
    return (
        <div className={styles.form}>
            <FC.Input
                readOnly
                contentBefore="所属租户"
                value={tenantName || `Tenant ${fixedTenantId}`}
            />
            {isPlatformAdminTarget && (
                <FC.MessageBar intent="warning">
                    <FC.MessageBarBody>
                        该账号是平台管理员。此处的编辑不会授予或撤销平台管理权限，停用也必须走专用的平台管理员安全动作。
                    </FC.MessageBarBody>
                </FC.MessageBar>
            )}
            <FC.Input contentBefore={"用户名"} autoComplete="off" {...v("account")} />
            <FC.Input contentBefore={"姓名"} autoComplete="off" {...v("name")} />
            <FC.Input contentBefore={"部门"} autoComplete="off" {...v("department")} />
            <FC.Input
                type="password"
                contentBefore={"密码"}
                autoComplete="new-password"
                {...v("password")}
            />
            <FC.Field label={"角色"}>
                <FC.Dropdown
                    contentBefore="角色"
                    label="角色"
                    labelPosition="above"
                    multiselect
                    selectedOptions={selectedGroups}
                    onOptionSelect={(evt, data) => {
                        updateInfo({ groups: sortGroups(data.selectedOptions) });
                    }}
                >
                    {groupOptions.map((option) => (
                        <FC.Option key={option.value} value={option.value}>
                            {option.label}
                        </FC.Option>
                    ))}
                </FC.Dropdown>
            </FC.Field>
            <FC.Switch
                label="禁用"
                labelPosition="above"
                defaultChecked={item?.disabled}
                onChange={(evt, data) => updateInfo({ disabled: data.checked })}
            />
            <div
                style={{
                    display:
                        (selectedGroups.includes("FieldOperator") && "block") ||
                        "none",
                }}
            >
                <FC.Label>风场权限（该人员可操作的风场范围）</FC.Label>
                <SimpleGrid
                    items={items}
                    columns={columns}
                    selectionMode="multiselect"
                    defaultSelectedItems={item?.fields.map((it) =>
                        idString(it),
                    )}
                    onSelectionChange={(evt, data) =>
                        onFieldsChange(data.selectedItems)
                    }
                    getRowId={(item) => idString(item.id)}
                />
            </div>
            <FC.Label>备注</FC.Label>
            <FC.Textarea {...v("description")} />
            <FC.Divider />
            {item && !isPlatformAdminTarget && <DeleteButton onClick={onDelete} />}
            <FC.Button appearance="primary" onClick={onSave} disabled={!info}>
                {(item && "保存") || "创建"}
            </FC.Button>
        </div>
    );
};
/**
 * People list for ONE tenant. There is no tenant-less mode any more: the only mount point is the
 * tenant workbench, where `/system/tenants/{tenantId}/manage` supplies the tenant. The list now
 * includes platform administrators — FRK-INTERNAL could not previously see its own staff — and
 * marks them, because an administrator row obeys different rules than an ordinary person row.
 */
export const UserManagement = ({ tenantId = null, tenantName = "" }) => {
    const fixedTenantId = Number.isInteger(Number(tenantId)) && Number(tenantId) > 0
        ? Number(tenantId)
        : null;
    const listItems = useCallback(async (client, filter) => {
        if (!fixedTenantId) {
            return [];
        }
        const people = await apiClient.getManagedPeople(fixedTenantId);
        if (!filter?.trim()) return people || [];
        const term = filter.trim().toLowerCase();
        return (people || []).filter((person) =>
            [person.account, person.name, person.department]
                .some((value) => value?.toLowerCase().includes(term))
        );
    }, [fixedTenantId]);
    const { items, SearchInput, refresh } = useSimpleGrid({
        listItems,
        placeholder: "按用户名、姓名、部门搜索...",
    });
    const { openDrawer, closeDrawer, drawer } = useDrawer();
    const onFormChange = () => {
        refresh();
        closeDrawer();
    };
    const onNewUser = () => {
        openDrawer({
            header: "创建用户",
            body: (
                <UserForm
                    item={null}
                    tenantId={fixedTenantId}
                    tenantName={tenantName}
                    onChange={onFormChange}
                />
            ),
        });
    };
    const columns = [
        UserDef.createTableColumn("account"),
        UserDef.createTableColumn("name"),
        FC.createTableColumn({
            columnId: "isPlatformAdmin",
            renderHeaderCell: () => "平台管理员",
            renderCell: (item) => (
                <FC.TableCellLayout>
                    {item.isPlatformAdmin ? (
                        <FC.Badge appearance="tint" color="brand" size="small">
                            是
                        </FC.Badge>
                    ) : (
                        ""
                    )}
                </FC.TableCellLayout>
            ),
        }),
        UserDef.createTableColumn("department"),
        UserDef.createTableColumn("role"),
        UserDef.createTableColumn("updated_at"),
        UserDef.createTableColumn("last_login_at"),
        UserDef.createTableColumn("description"),
        UserDef.createTableColumn("disabled"),
        UserDef.createOpsColumn({
            renderCell: (item) => {
                const onClick = () => {
                    openDrawer({
                        header: "用户详情",
                        body: (
                            <UserForm
                                item={item}
                                tenantId={fixedTenantId}
                                tenantName={tenantName}
                                onChange={onFormChange}
                            />
                        ),
                    });
                };
                return (
                    <FC.TableCellLayout>
                        <FC.Button
                            icon={<IC.MoreCircleFilled />}
                            appearance="subtle"
                            onClick={onClick}
                        />
                    </FC.TableCellLayout>
                );
            },
        }),
    ];

    const styles = useStyles();
    if (!fixedTenantId) {
        return <div className={styles.root}>请先选择租户，再管理该租户的人员。</div>;
    }

    return (
        <div className={styles.root}>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                    alignItems: "center",
                }}
            >
                <FC.Label>快速检索</FC.Label>
                <SearchInput />
                <FC.Button appearance="primary" onClick={onNewUser}>
                    创建用户
                </FC.Button>
            </div>
            <SimpleGrid items={items} columns={columns} />
            {drawer}
        </div>
    );
};
